import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info,
  FileText,
  Zap,
  Sun,
  Cable,
  Radio,
  BarChart3,
  MapPin,
  Calendar,
  ChevronDown,
  ChevronRight,
  Loader2,
  ExternalLink,
  Library,
} from 'lucide-react';
import type { IDockviewPanelProps } from 'dockview';
import { useAppSelector } from '../../redux/hooks';
import {
  useGetSiteValidationResultsQuery,
  useGetSiteDrawingInconsistenciesQuery,
  useGetSiteMetadataQuery,
  type ValidationResults,
  type DrawingInconsistency,
  type SiteMetadata,
  type ValidatorDetail,
} from '../../redux/api/portfolioApi';

// Validators we want to display
const DISPLAY_VALIDATORS = [
  'metadata_counts',
  'power_flow_topology',
  'graph_build',
  'uniform_string_inputs',
  'component_library_match',
  'communications_monitoring',
];

// Human-readable validator names
const VALIDATOR_LABELS: Record<string, string> = {
  metadata_counts: 'Metadata Counts',
  power_flow_topology: 'Power Flow Topology',
  graph_build: 'Graph Build',
  uniform_string_inputs: 'Power Inputs',
  component_library_match: 'Component Library Check',
  communications_monitoring: 'Communications & Monitoring',
};

// Validator icons
const VALIDATOR_ICONS: Record<string, React.ReactNode> = {
  metadata_counts: <BarChart3 className="w-4 h-4" />,
  power_flow_topology: <Zap className="w-4 h-4" />,
  graph_build: <Cable className="w-4 h-4" />,
  uniform_string_inputs: <Sun className="w-4 h-4" />,
  component_library_match: <Library className="w-4 h-4" />,
  communications_monitoring: <Radio className="w-4 h-4" />,
};

// Helper to generate Google Maps URL
const getGoogleMapsUrl = (address: string): string => {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
};

// Grouped dropdown list for inverter string counts
// Input: { "INV-001": 8, "INV-002": 9, "INV-003": 8, ... } (inverter name -> string count)
// Groups inverters by their string count, with expandable dropdown showing inverter names
interface InverterStringCountsListProps {
  counts: Record<string, number>;
}

const InverterStringCountsList: React.FC<InverterStringCountsListProps> = ({ counts }) => {
  const [expandedGroups, setExpandedGroups] = React.useState<Set<number>>(new Set());

  // Group inverters by their string count
  const groupedByStringCount = React.useMemo(() => {
    const groups: Record<number, string[]> = {};
    Object.entries(counts).forEach(([inverterName, stringCount]) => {
      if (!groups[stringCount]) {
        groups[stringCount] = [];
      }
      groups[stringCount].push(inverterName);
    });
    // Sort inverter names within each group
    Object.values(groups).forEach(inverters => inverters.sort());
    return groups;
  }, [counts]);

  // Get sorted string counts (keys)
  const sortedStringCounts = Object.keys(groupedByStringCount)
    .map(Number)
    .sort((a, b) => a - b);

  const totalInverters = Object.keys(counts).length;

  const toggleGroup = (stringCount: number) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(stringCount)) {
        next.delete(stringCount);
      } else {
        next.add(stringCount);
      }
      return next;
    });
  };

  return (
    <div className="bg-[var(--color-surface)] rounded-lg p-3">
      <h4 className="text-xs font-semibold eg-text-secondary uppercase mb-2">
        Inverter Inputs ({totalInverters} total)
      </h4>
      <div className="space-y-1">
        {sortedStringCounts.map((stringCount) => {
          const inverters = groupedByStringCount[stringCount];
          const isExpanded = expandedGroups.has(stringCount);
          const inputsLabel = stringCount === 1 ? 'input' : 'inputs';

          return (
            <div key={stringCount} className="rounded overflow-hidden border border-[var(--color-border)]">
              <button
                onClick={() => toggleGroup(stringCount)}
                className="w-full flex items-center justify-between py-2 px-3 bg-[var(--color-surface-hover)] hover:bg-[var(--color-surface-hover)] text-sm transition-colors"
              >
                <div className="flex items-center gap-2">
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 eg-text-secondary" />
                  ) : (
                    <ChevronRight className="w-4 h-4 eg-text-secondary" />
                  )}
                  <span className="eg-text-primary font-medium">
                    {stringCount} {inputsLabel}
                  </span>
                </div>
                <span className="px-2 py-0.5 bg-engramiq-green/20 text-engramiq-green text-xs rounded-full font-medium">
                  {inverters.length} inverter{inverters.length !== 1 ? 's' : ''}
                </span>
              </button>
              {isExpanded && (
                <div className="py-2 px-3 bg-[var(--color-surface)] border-t border-[var(--color-border)]">
                  <div className="flex flex-wrap gap-1.5">
                    {inverters.map((inverterName) => (
                      <span
                        key={inverterName}
                        className="px-2 py-1 bg-[var(--color-surface-hover)] eg-text-primary text-xs rounded"
                      >
                        {inverterName}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Grouped dropdown list for combiner string counts
// Input: { "CB-1A.1": 12, "CB-1A.2": 12, ... } (combiner name -> string count)
// Groups combiners by their string count, with expandable dropdown showing combiner names
interface CombinerInputsListProps {
  counts: Record<string, number>;
}

const CombinerInputsList: React.FC<CombinerInputsListProps> = ({ counts }) => {
  const [expandedGroups, setExpandedGroups] = React.useState<Set<number>>(new Set());

  // Group combiners by their string count
  const groupedByStringCount = React.useMemo(() => {
    const groups: Record<number, string[]> = {};
    Object.entries(counts).forEach(([combinerName, stringCount]) => {
      if (!groups[stringCount]) {
        groups[stringCount] = [];
      }
      groups[stringCount].push(combinerName);
    });
    // Sort combiner names within each group
    Object.values(groups).forEach(combiners => combiners.sort());
    return groups;
  }, [counts]);

  // Get sorted string counts (keys)
  const sortedStringCounts = Object.keys(groupedByStringCount)
    .map(Number)
    .sort((a, b) => a - b);

  const totalCombiners = Object.keys(counts).length;

  const toggleGroup = (stringCount: number) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(stringCount)) {
        next.delete(stringCount);
      } else {
        next.add(stringCount);
      }
      return next;
    });
  };

  return (
    <div className="bg-[var(--color-surface)] rounded-lg p-3">
      <h4 className="text-xs font-semibold eg-text-secondary uppercase mb-2">
        Combiner Inputs ({totalCombiners} total)
      </h4>
      <div className="space-y-1">
        {sortedStringCounts.map((stringCount) => {
          const combiners = groupedByStringCount[stringCount];
          const isExpanded = expandedGroups.has(stringCount);
          const inputsLabel = stringCount === 1 ? 'input' : 'inputs';

          return (
            <div key={stringCount} className="rounded overflow-hidden border border-[var(--color-border)]">
              <button
                onClick={() => toggleGroup(stringCount)}
                className="w-full flex items-center justify-between py-2 px-3 bg-[var(--color-surface-hover)] hover:bg-[var(--color-surface-hover)] text-sm transition-colors"
              >
                <div className="flex items-center gap-2">
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 eg-text-secondary" />
                  ) : (
                    <ChevronRight className="w-4 h-4 eg-text-secondary" />
                  )}
                  <span className="eg-text-primary font-medium">
                    {stringCount} {inputsLabel}
                  </span>
                </div>
                <span className="px-2 py-0.5 bg-engramiq-green/20 text-engramiq-green text-xs rounded-full font-medium">
                  {combiners.length} combiner{combiners.length !== 1 ? 's' : ''}
                </span>
              </button>
              {isExpanded && (
                <div className="py-2 px-3 bg-[var(--color-surface)] border-t border-[var(--color-border)]">
                  <div className="flex flex-wrap gap-1.5">
                    {combiners.map((combinerName) => (
                      <span
                        key={combinerName}
                        className="px-2 py-1 bg-[var(--color-surface-hover)] eg-text-primary text-xs rounded"
                      >
                        {combinerName}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface ValidatorCardProps {
  name: string;
  detail: ValidatorDetail;
  isExpanded: boolean;
  onToggle: () => void;
}

const ValidatorCard: React.FC<ValidatorCardProps> = ({ name, detail, isExpanded, onToggle }) => {
  const hasErrors = detail.errors.length > 0;
  const hasWarnings = detail.warnings.length > 0;

  const statusIcon = hasErrors ? (
    <XCircle className="w-5 h-5 text-accent-red" />
  ) : hasWarnings ? (
    <AlertTriangle className="w-5 h-5 text-yellow-500" />
  ) : (
    <CheckCircle className="w-5 h-5 text-engramiq-green" />
  );

  const statusBg = hasErrors
    ? 'border-accent-red/30 bg-accent-red/5'
    : hasWarnings
    ? 'border-yellow-500/30 bg-yellow-500/5'
    : 'border-engramiq-green/30 bg-engramiq-green/5';

  return (
    <div className={`eg-card-elevated ${statusBg} overflow-hidden`}>
      <button
        onClick={onToggle}
        className="w-full p-4 flex items-center justify-between hover:bg-[var(--color-surface-hover)] transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-engramiq-green">
            {VALIDATOR_ICONS[name] || <Info className="w-4 h-4" />}
          </span>
          <span className="font-medium eg-text-primary">
            {VALIDATOR_LABELS[name] || name}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {statusIcon}
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 eg-text-secondary" />
          ) : (
            <ChevronRight className="w-4 h-4 eg-text-secondary" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-3">
          {/* Stats */}
          {detail.stats && Object.keys(detail.stats).length > 0 && (
            <div className="bg-[var(--color-surface)] rounded-lg p-3">
              <h4 className="text-xs font-semibold eg-text-secondary uppercase mb-2">Statistics</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {Object.entries(detail.stats).map(([key, value]) => {
                  // Skip complex objects (handled separately below)
                  if (typeof value === 'object' && value !== null) return null;
                  return (
                    <div key={key} className="text-sm">
                      <span className="eg-text-secondary">{key.replace(/_/g, ' ')}: </span>
                      <span className="eg-text-primary font-medium">
                        {typeof value === 'number' ? value.toLocaleString() : String(value)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Inverter Inputs - collapsible list for uniform_string_inputs */}
          {name === 'uniform_string_inputs' && detail.stats?.inverter_string_counts && (
            <InverterStringCountsList counts={detail.stats.inverter_string_counts as Record<string, number>} />
          )}

          {/* Combiner Inputs - collapsible list for uniform_string_inputs, only shown if present */}
          {name === 'uniform_string_inputs' && detail.stats?.combiner_string_counts && (
            <CombinerInputsList counts={detail.stats.combiner_string_counts as Record<string, number>} />
          )}

          {/* Validation Details */}
          {detail.validation_details && Object.keys(detail.validation_details).length > 0 && (
            <div className="bg-[var(--color-surface)] rounded-lg p-3">
              <h4 className="text-xs font-semibold eg-text-secondary uppercase mb-2">Validation Details</h4>
              <div className="space-y-2">
                {Object.entries(detail.validation_details).map(([key, value]) => {
                  if (!value || typeof value !== 'object') return null;
                  const v = value as Record<string, any>;
                  const actual = v.actual;
                  const expected = v.expected;
                  const isMatch = actual === expected || expected === null;

                  return (
                    <div key={key} className="flex items-center justify-between text-sm">
                      <span className="eg-text-secondary capitalize">{key.replace(/_/g, ' ')}</span>
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${isMatch ? 'text-engramiq-green' : 'text-yellow-500'}`}>
                          {typeof actual === 'number' ? actual.toLocaleString() : String(actual ?? 'N/A')}
                        </span>
                        {expected !== null && expected !== undefined && (
                          <span className="eg-text-secondary">
                            / {typeof expected === 'number' ? expected.toLocaleString() : String(expected)} expected
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Errors */}
          {detail.errors.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-accent-red uppercase">Errors ({detail.errors.length})</h4>
              {detail.errors.map((error, idx) => (
                <div key={idx} className="flex items-start gap-2 p-2 bg-accent-red/10 rounded border border-accent-red/20">
                  <XCircle className="w-4 h-4 text-accent-red flex-shrink-0 mt-0.5" />
                  <p className="text-sm eg-text-primary">{error}</p>
                </div>
              ))}
            </div>
          )}

          {/* Warnings */}
          {detail.warnings.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-yellow-500 uppercase">Warnings ({detail.warnings.length})</h4>
              {detail.warnings.map((warning, idx) => (
                <div key={idx} className="flex items-start gap-2 p-2 bg-yellow-500/10 rounded border border-yellow-500/20">
                  <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm eg-text-primary">{warning}</p>
                </div>
              ))}
            </div>
          )}

          {/* No issues message */}
          {detail.errors.length === 0 && detail.warnings.length === 0 && (
            <div className="flex items-center gap-2 p-2 bg-engramiq-green/10 rounded border border-[var(--color-border)]">
              <CheckCircle className="w-4 h-4 text-engramiq-green" />
              <p className="text-sm eg-text-primary">No issues detected</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Clickable page chip component
interface PageChipProps {
  page: number;
  nodeId?: string;
  label?: string;
  variant?: 'used' | 'conflict';
  onNavigate: (page: number, nodeId?: string, nodeLabel?: string) => void;
}

const PageChip: React.FC<PageChipProps> = ({
  page,
  nodeId,
  label,
  variant = 'used',
  onNavigate
}) => {
  const baseClasses = "px-2 py-0.5 text-xs rounded-full cursor-pointer transition-colors font-medium";
  const variantClasses = variant === 'used'
    ? "bg-engramiq-green/20 text-engramiq-green hover:bg-engramiq-green/30"
    : "bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/30";

  return (
    <button
      onClick={() => onNavigate(page, nodeId, label)}
      className={`${baseClasses} ${variantClasses}`}
      title={`Go to page ${page}`}
    >
      p{page}
    </button>
  );
};

interface InconsistencyCardProps {
  inconsistency: DrawingInconsistency;
  index: number;
  onNavigate: (page: number, nodeId?: string, nodeLabel?: string) => void;
}

const InconsistencyCard: React.FC<InconsistencyCardProps> = ({ inconsistency, index, onNavigate }) => {
  return (
    <div className="eg-card-elevated border-yellow-500/30 p-4">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center">
          <span className="text-sm font-bold text-yellow-500">{index + 1}</span>
        </div>
        <div className="flex-1 space-y-2">
          <p className="text-sm eg-text-primary leading-relaxed">
            {inconsistency.description}
          </p>
          <div className="flex flex-wrap items-center gap-3 text-xs eg-text-secondary">
            {inconsistency.node_id && (
              <span className="flex items-center gap-1">
                <span className="text-engramiq-green">Node:</span> {inconsistency.node_id}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <span className="text-engramiq-green">Page used:</span>
              <PageChip
                page={inconsistency.page_used}
                nodeId={inconsistency.node_id ?? undefined}
                label={inconsistency.node_id || `Inconsistency ${index + 1}`}
                variant="used"
                onNavigate={onNavigate}
              />
            </span>
            {inconsistency.conflicting_pages.length > 0 && (
              <span className="flex items-center gap-1.5">
                <span className="text-engramiq-green">Conflicting:</span>
                {inconsistency.conflicting_pages.map((page, i) => (
                  <PageChip
                    key={i}
                    page={page}
                    nodeId={inconsistency.node_id ?? undefined}
                    label={`${inconsistency.node_id || 'Conflict'} (page ${page})`}
                    variant="conflict"
                    onNavigate={onNavigate}
                  />
                ))}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface ExtractionDataTabProps {
  api?: IDockviewPanelProps['api'];
}

const ExtractionDataTab: React.FC<ExtractionDataTabProps> = ({ api }) => {
  const [expandedValidators, setExpandedValidators] = React.useState<Set<string>>(new Set()); // Start collapsed

  // Use route to determine if queries should run
  // ExtractionDataTab is only relevant on certain routes, not on component-view
  const location = useLocation();
  const routesThatShouldNotFetch = ['/component-view', '/component-metadata'];
  const isOnIrrelevantRoute = routesThatShouldNotFetch.includes(location.pathname);

  // Track panel visibility to skip queries when not visible
  // Default to false to prevent queries on mount - only fetch when explicitly activated
  const [isPanelActive, setIsPanelActive] = useState(false);
  const hasReceivedActiveEvent = React.useRef(false);

  useEffect(() => {
    if (!api) {
      // No api means we can't track visibility - default to visible for backwards compatibility
      setIsPanelActive(true);
      return;
    }

    // Subscribe to visibility changes - only trust events, not initial state
    // This prevents queries when dockview mounts panels during initialization
    const disposable = api.onDidActiveChange((event) => {
      hasReceivedActiveEvent.current = true;
      setIsPanelActive(event.isActive);
    });

    // Check initial state only after a delay, and only if we haven't received an event yet
    const timeoutId = setTimeout(() => {
      if (!hasReceivedActiveEvent.current && api.isActive) {
        setIsPanelActive(true);
      }
    }, 200);

    return () => {
      clearTimeout(timeoutId);
      disposable.dispose();
    };
  }, [api]);

  // Combine route check with panel active state
  const isPanelVisible = isPanelActive && !isOnIrrelevantRoute;

  // Get current site from Redux
  const currentSite = useAppSelector((state) => state.site.currentSite);
  const portfolioId = currentSite?.portfolio_id;
  const siteId = currentSite?.site_id || currentSite?.id;

  // Navigate to a page in the as-built viewer
  const navigateToAsBuiltPage = useCallback((page: number, nodeId?: string, nodeLabel?: string) => {
    window.dispatchEvent(new CustomEvent('navigateToAsBuiltPage', {
      detail: {
        nodeId: nodeId || `page-${page}`,
        nodeLabel: nodeLabel || `Page ${page}`,
        page,
        pageRefs: [{ label: nodeLabel || `Page ${page}`, page }]
      }
    }));
  }, []);

  // Skip queries when panel is not visible (prevents unnecessary requests when Dockview mounts hidden panels)
  const shouldSkipQueries = !portfolioId || !siteId || !isPanelVisible;

  // Fetch data (only when panel is visible)
  const {
    data: validationResults,
    isLoading: validationLoading,
    error: validationError
  } = useGetSiteValidationResultsQuery(
    { portfolio_id: portfolioId!, site_id: siteId! },
    { skip: shouldSkipQueries }
  );

  const {
    data: inconsistenciesData,
    isLoading: inconsistenciesLoading,
    error: inconsistenciesError
  } = useGetSiteDrawingInconsistenciesQuery(
    { portfolio_id: portfolioId!, site_id: siteId! },
    { skip: shouldSkipQueries }
  );

  const {
    data: siteMetadata,
    isLoading: metadataLoading,
    error: metadataError
  } = useGetSiteMetadataQuery(
    { portfolio_id: portfolioId!, site_id: siteId! },
    { skip: shouldSkipQueries }
  );

  const isLoading = validationLoading || inconsistenciesLoading || metadataLoading;

  // Calculate validation summary
  const validationSummary = useMemo(() => {
    if (!validationResults || !validationResults.validator_details) return null;

    const displayedValidators = DISPLAY_VALIDATORS.filter(
      v => validationResults.validator_details?.[v]
    );

    let errors = 0;
    let warnings = 0;
    let passed = 0;

    displayedValidators.forEach(name => {
      const detail = validationResults.validator_details?.[name];
      if (!detail) return;
      if (detail.errors.length > 0) errors++;
      else if (detail.warnings.length > 0) warnings++;
      else passed++;
    });

    return { errors, warnings, passed, total: displayedValidators.length };
  }, [validationResults]);

  const toggleValidator = (name: string) => {
    setExpandedValidators(prev => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  // No site selected
  if (!currentSite || !portfolioId || !siteId) {
    return (
      <div className="eg-page-shell flex items-center justify-center">
        <div className="text-center p-8">
          <FileText className="w-12 h-12 eg-text-secondary mx-auto mb-4" />
          <h2 className="text-xl font-semibold eg-text-primary mb-2">No Site Selected</h2>
          <p className="eg-text-secondary">
            Please select a site from the Sites List to view extraction data.
          </p>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="eg-page-shell flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-engramiq-green animate-spin mx-auto mb-4" />
          <p className="eg-text-primary">Loading extraction data...</p>
        </div>
      </div>
    );
  }

  const inconsistencies = inconsistenciesData?.inconsistencies || [];

  return (
    <div className="eg-page-shell flex flex-col overflow-auto">
      {/* Header */}
      <div className="eg-page-header relative">
        <div className="eg-page-header-glow" />
        <div className="px-6 pt-5 pb-4">
          <h1 className="text-2xl font-bold eg-text-primary mb-2">
            Extraction Report
          </h1>
          <p className="eg-text-secondary">
            {currentSite.site_name || currentSite.name || 'Unknown Site'}
          </p>
        </div>
        <div className="eg-page-accent-divider" />
      </div>

      <div className="eg-page-content flex-1">
      <div className="px-6 py-5 max-w-5xl mx-auto space-y-8">

        {/* Site Metadata Section */}
        <section>
          <h2 className="text-lg font-semibold eg-text-primary mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-engramiq-green" />
            Site Metadata
          </h2>

          {metadataError ? (
            <div className="bg-[var(--color-surface)] border border-accent-red/30 rounded-lg p-4">
              <p className="text-accent-red text-sm">Failed to load site metadata</p>
            </div>
          ) : siteMetadata ? (
            <div className="eg-card-elevated p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Drawing Info */}
                  <div className="space-y-3">
                    <h3 className="text-xs font-semibold eg-text-secondary uppercase tracking-wider">Drawing Information</h3>
                    <div className="space-y-2">
                      {siteMetadata.drawing_type && (
                        <div className="flex items-center gap-2 text-sm">
                          <FileText className="w-4 h-4 text-engramiq-green" />
                          <span className="eg-text-secondary">Type:</span>
                          <span className="eg-text-primary font-medium">{siteMetadata.drawing_type}</span>
                        </div>
                      )}
                      {siteMetadata.drawing_date && (
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-engramiq-green" />
                          <span className="eg-text-secondary">Date:</span>
                          <span className="eg-text-primary font-medium">{siteMetadata.drawing_date}</span>
                        </div>
                      )}
                      {siteMetadata.site_address && (
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin className="w-4 h-4 text-engramiq-green" />
                          <span className="eg-text-secondary">Address:</span>
                          <a
                            href={getGoogleMapsUrl(siteMetadata.site_address)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-engramiq-green hover:text-engramiq-green/80 font-medium inline-flex items-center gap-1 hover:underline"
                          >
                            {siteMetadata.site_address}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Capacity */}
                  <div className="space-y-3">
                    <h3 className="text-xs font-semibold eg-text-secondary uppercase tracking-wider">System Capacity</h3>
                    <div className="space-y-2">
                      {siteMetadata.dc_capacity_kw !== null && (
                        <div className="flex items-center gap-2 text-sm">
                          <Zap className="w-4 h-4 text-engramiq-green" />
                          <span className="eg-text-secondary">DC Capacity:</span>
                          <span className="eg-text-primary font-medium">{siteMetadata.dc_capacity_kw.toLocaleString()} kW</span>
                        </div>
                      )}
                      {siteMetadata.ac_capacity_kw !== null && (
                        <div className="flex items-center gap-2 text-sm">
                          <Zap className="w-4 h-4 text-engramiq-green" />
                          <span className="eg-text-secondary">AC Capacity:</span>
                          <span className="eg-text-primary font-medium">{siteMetadata.ac_capacity_kw.toLocaleString()} kW</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Component Counts */}
                  <div className="space-y-3">
                    <h3 className="text-xs font-semibold eg-text-secondary uppercase tracking-wider">Components</h3>
                    <div className="space-y-2">
                      {siteMetadata.number_of_modules != null && (
                        <div className="flex items-center gap-2 text-sm">
                          <Sun className="w-4 h-4 text-engramiq-green" />
                          <span className="eg-text-secondary">Modules:</span>
                          <span className="eg-text-primary font-medium">{siteMetadata.number_of_modules.toLocaleString()}</span>
                        </div>
                      )}
                      {siteMetadata.number_of_strings != null && (
                        <div className="flex items-center gap-2 text-sm">
                          <Cable className="w-4 h-4 text-engramiq-green" />
                          <span className="eg-text-secondary">Strings:</span>
                          <span className="eg-text-primary font-medium">{siteMetadata.number_of_strings.toLocaleString()}</span>
                        </div>
                      )}
                      {siteMetadata.number_of_inverters != null && (
                        <div className="flex items-center gap-2 text-sm">
                          <Zap className="w-4 h-4 text-engramiq-green" />
                          <span className="eg-text-secondary">Inverters:</span>
                          <span className="eg-text-primary font-medium">{siteMetadata.number_of_inverters.toLocaleString()}</span>
                        </div>
                      )}
                      {siteMetadata.modules_per_string != null && (
                        <div className="flex items-center gap-2 text-sm">
                          <BarChart3 className="w-4 h-4 text-engramiq-green" />
                          <span className="eg-text-secondary">Modules/String:</span>
                          <span className="eg-text-primary font-medium">{siteMetadata.modules_per_string}</span>
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4">
              <p className="eg-text-secondary text-sm">No metadata available</p>
            </div>
          )}
        </section>

        {/* Validation Results Section */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold eg-text-primary flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-engramiq-green" />
              Validation Results
            </h2>

            {validationSummary && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-engramiq-green font-medium">{validationSummary.passed} passed</span>
                {validationSummary.warnings > 0 && (
                  <>
                    <span className="eg-text-secondary">,</span>
                    <span className="text-yellow-500 font-medium">
                      {validationSummary.warnings} {validationSummary.warnings === 1 ? 'category' : 'categories'} with warnings
                    </span>
                  </>
                )}
              </div>
            )}
          </div>

          {validationError ? (
            <div className="bg-[var(--color-surface)] border border-accent-red/30 rounded-lg p-4">
              <p className="text-accent-red text-sm">Failed to load validation results</p>
            </div>
          ) : validationResults ? (
            <div className="space-y-3">
              {/* Validator cards */}
              {validationResults.validator_details && DISPLAY_VALIDATORS.map(name => {
                const detail = validationResults.validator_details?.[name];
                if (!detail) return null;
                return (
                  <ValidatorCard
                    key={name}
                    name={name}
                    detail={detail}
                    isExpanded={expandedValidators.has(name)}
                    onToggle={() => toggleValidator(name)}
                  />
                );
              })}
            </div>
          ) : (
            <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4">
              <p className="eg-text-secondary text-sm">No validation results available</p>
            </div>
          )}
        </section>

        {/* Drawing Inconsistencies Section */}
        <section>
          <h2 className="text-lg font-semibold eg-text-primary mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            Drawing Inconsistencies
            {inconsistencies.length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-yellow-500/20 text-yellow-500 text-xs rounded-full">
                {inconsistencies.length}
              </span>
            )}
          </h2>

          {inconsistenciesError ? (
            <div className="bg-[var(--color-surface)] border border-accent-red/30 rounded-lg p-4">
              <p className="text-accent-red text-sm">Failed to load drawing inconsistencies</p>
            </div>
          ) : inconsistencies.length > 0 ? (
            <div className="space-y-3">
              {inconsistencies.map((inc, idx) => (
                <InconsistencyCard key={idx} inconsistency={inc} index={idx} onNavigate={navigateToAsBuiltPage} />
              ))}
            </div>
          ) : (
            <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-engramiq-green" />
                <p className="eg-text-primary text-sm">No drawing inconsistencies detected</p>
              </div>
            </div>
          )}
        </section>
      </div>
      </div>
    </div>
  );
};

export default ExtractionDataTab;
